import React, { useState } from 'react';
import { Sparkles, Loader, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

const API_BASE_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:4000'
  : window.location.origin;

const ExplainButton = ({ control }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [explanation, setExplanation] = useState(null);
  const [error, setError] = useState(null);

  const fetchExplanation = async () => {
    if (explanation) {
      // Already have explanation, just toggle
      setIsExpanded(!isExpanded);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      console.log(control);
      const response = await fetch(`${API_BASE_URL}/api/ai-explain-control`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          control_id: control.controlId,
          control_name: control.controlName,
          framework_name: control.frameworkName || 'Compliance Framework',
          finding: control.finding || 'Control failed compliance check',
          priority: control.priority || 'Unknown'
        })
      });

      const data = await response.json();

      if (data.success) {
        setExplanation(data);
        setIsExpanded(true);
      } else {
        throw new Error(data.error || 'Failed to get explanation');
      }
    } catch (err) {
      console.error('Error fetching explanation:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mt-3">
      {/* Explain Button */}
      <button
        onClick={fetchExplanation}
        disabled={isLoading}
        className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white text-sm font-medium rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isLoading ? (
          <><Loader className="w-4 h-4 animate-spin" /> Generating...</>
        ) : (
          <>
            <Sparkles className="w-4 h-4" />
            {explanation ? (isExpanded ? 'Hide' : 'Show') + ' Explanation' : 'Explain Why This Failed'}
            {explanation && (isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />)}
          </>
        )}
      </button>

      {/* Error State */}
      {error && (
        <div className="mt-3 p-4 bg-red-50 border-l-4 border-red-500 rounded-r-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-900">Failed to generate explanation</p>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Explanation Content */}
      {explanation && isExpanded && (
        <div className="mt-4 bg-gradient-to-br from-purple-50 to-blue-50 border-2 border-purple-200 rounded-xl p-6 shadow-lg animate-fadeIn">
          {/* Header */}
          <div className="flex items-center justify-between mb-4 pb-4 border-b-2 border-purple-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl flex items-center justify-center shadow-md">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <h4 className="text-lg font-bold text-gray-900">AI Explanation</h4>
                <p className="text-xs text-gray-600">{control.controlId} - {control.controlName}</p>
              </div>
            </div>
            {explanation.cached && (
              <span className="flex items-center gap-1.5 px-3 py-1.5 bg-green-100 text-green-800 text-xs font-semibold rounded-full">
                <Sparkles className="w-3.5 h-3.5" />
                Instant (Cached)
              </span>
            )}
          </div>

          {/* Explanation Content */}
          <div className="prose prose-sm max-w-none">
            <ReactMarkdown
              components={{
                p: ({ node, ...props }) => (
                  <p className="text-gray-800 text-sm leading-relaxed my-3 first:mt-0" {...props} />
                ),
                strong: ({ node, ...props }) => (
                  <strong className="font-bold text-gray-900" {...props} />
                ),
                ul: ({ node, ...props }) => (
                  <ul className="list-disc pl-5 my-3 space-y-1.5" {...props} />
                ),
                ol: ({ node, ...props }) => (
                  <ol className="list-decimal pl-5 my-3 space-y-1.5" {...props} />
                ),
                li: ({ node, ...props }) => (
                  <li className="text-gray-800 text-sm" {...props} />
                ),
                h1: ({ node, ...props }) => (
                  <h1 className="text-base font-bold text-gray-900 mt-4 mb-2" {...props} />
                ),
                h2: ({ node, ...props }) => (
                  <h2 className="text-base font-bold text-gray-900 mt-4 mb-2" {...props} />
                ),
                h3: ({ node, ...props }) => (
                  <h3 className="text-sm font-semibold text-gray-900 mt-3 mb-1.5" {...props} />
                ),
                code: ({ node, inline, ...props }) =>
                  inline ? (
                    <code className="bg-purple-100 text-purple-800 px-1.5 py-0.5 rounded text-xs font-mono" {...props} />
                  ) : (
                    <code className="block bg-gray-100 p-3 rounded text-xs font-mono my-2" {...props} />
                  )
              }}
            >
              {explanation.explanation}
            </ReactMarkdown>
          </div>

          {/* Footer */}
          {!explanation.cached && (
            <div className="mt-4 pt-4 border-t-2 border-purple-200 flex items-center justify-between">
              <span className="text-xs text-gray-600 font-medium">
                AI-powered explanation by GPT-4o-mini
              </span>
              <span className="text-xs text-gray-500">
                {explanation.tokens_used} tokens • ${explanation.cost_usd?.toFixed(4)}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ExplainButton;
